// src/components/mdx/client/visualizations/blogChartPalette.ts

/**
 * Shared colour references for the interactive blog charts.
 *
 * We intentionally map back to the canonical chart colour utilities so the
 * palette stays aligned with other dashboards/components that rely on the same
 * naming. This avoids situations where updating a brand token leaves a chart
 * behind because it hard-coded a hex value.
 */

import { chartColorUtils } from '~/lib/charts/colors'

const { getHexColor } = chartColorUtils

const hexToRgba = (hex: string, alpha: number): string => {
  const normalized = hex.replace('#', '')
  const bigint = Number.parseInt(normalized, 16)
  const r = (bigint >> 16) & 255
  const g = (bigint >> 8) & 255
  const b = bigint & 255
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Hex strings for the three Signal vs Noise series.
 */
export const SIGNAL_NOISE_SERIES = {
  noisy: getHexColor('rose'),
  smoothing: getHexColor('emerald'),
  pure: getHexColor('indigo')
} as const

/**
 * Creative fatigue chart colours.
 */
export const CREATIVE_FATIGUE_SERIES = {
  roas: getHexColor('emerald'),
  movingAverage: getHexColor('violet')
} as const

export const CREATIVE_FATIGUE_PHASES = {
  stable: hexToRgba(getHexColor('emerald'), 0.12),
  falseDip: hexToRgba(getHexColor('orange'), 0.15),
  recovery: hexToRgba(getHexColor('cyan'), 0.15),
  fatigue: hexToRgba(getHexColor('rose'), 0.12)
} as const

/**
 * Moving average vs. purpose chart colours.
 */
export const MOVING_AVERAGE_SERIES = {
  informational: getHexColor('cyan'),
  conversion: getHexColor('amber'),
  balanced: getHexColor('purple')
} as const

export const MOVING_AVERAGE_HIGHLIGHTS = {
  high: getHexColor('emerald'),
  low: getHexColor('rose')
} as const

export const MOVING_AVERAGE_PHASES = {
  stable: hexToRgba(getHexColor('blue'), 0.12),
  trendUp: hexToRgba(getHexColor('emerald'), 0.12),
  falseDown: hexToRgba(getHexColor('amber'), 0.12),
  volatile: hexToRgba(getHexColor('fuchsia'), 0.12),
  trendDown: hexToRgba(getHexColor('rose'), 0.12)
} as const

/**
 * Substitution effect chart colours.
 */
export const SUBSTITUTION_SERIES = {
  branded: getHexColor('violet'),
  competitor: getHexColor('rose'),
  incremental: getHexColor('emerald')
} as const

export const SUBSTITUTION_PHASES = {
  fatiguedSpend: hexToRgba(getHexColor('rose'), 0.4),
  newSpend: hexToRgba(getHexColor('emerald'), 0.4),
  learningWindow: hexToRgba(getHexColor('blue'), 0.15)
} as const

/**
 * Neutral greys reused for grid/axis rendering.
 */
export const AXIS_TOKENS = {
  stroke: 'rgb(156, 163, 175)',
  grid: 'rgba(75, 85, 99, 0.2)'
} as const

export type SignalNoiseSeriesKey = keyof typeof SIGNAL_NOISE_SERIES
export type CreativeFatigueSeriesKey = keyof typeof CREATIVE_FATIGUE_SERIES
export type CreativeFatiguePhaseKey = keyof typeof CREATIVE_FATIGUE_PHASES
export type MovingAverageSeriesKey = keyof typeof MOVING_AVERAGE_SERIES
export type MovingAverageHighlightKey = keyof typeof MOVING_AVERAGE_HIGHLIGHTS
export type MovingAveragePhaseKey = keyof typeof MOVING_AVERAGE_PHASES
export type SubstitutionSeriesKey = keyof typeof SUBSTITUTION_SERIES
export type SubstitutionPhaseKey = keyof typeof SUBSTITUTION_PHASES

/**
 * Marketing data visualization blog chart colours.
 */
export const MARKETING_DATA_VIZ_SERIES = {
  mer: getHexColor('emerald'),
  metaRoas: getHexColor('cyan'),
  spend: getHexColor('amber'),
  primary: getHexColor('indigo'),
  secondary: getHexColor('violet'),
  accent: getHexColor('rose'),
  funnel: getHexColor('blue'),
  retention: getHexColor('purple'),
  benchmark: getHexColor('orange'),
  ci: getHexColor('gray')
} as const

export const MARKETING_DATA_VIZ_PHASES = {
  refresh: hexToRgba(getHexColor('emerald'), 0.15),
  thumbstop: hexToRgba(getHexColor('orange'), 0.2),
  thruplay: hexToRgba(getHexColor('cyan'), 0.2),
  cliff: hexToRgba(getHexColor('rose'), 0.15),
  cohortHigh: getHexColor('emerald'),
  cohortLow: getHexColor('rose')
} as const

export type MarketingDataVizSeriesKey = keyof typeof MARKETING_DATA_VIZ_SERIES